<template>
  <div class="summary-container" data-testid="kds-summary">
    <div class="summary-header">
      <h2>Resumen de Elementos</h2>
    </div>
    <div class="summary-content">
      <div v-if="aggregatedItems.length > 0" class="items-list">
        <div v-for="(item, index) in aggregatedItems" :key="index" class="summary-item" data-testid="kds-summary-item">
          <div class="item-emoji" v-html="getEmoji(item)"></div>
          <div class="item-count">{{ item.totalQuantity }}</div>
          <div class="item-name" translate="no">{{ item.display_string }}</div>
        </div>
      </div>
      <div v-else class="no-items" data-testid="kds-summary-empty">
        <p>No hay items para mostrar</p>
      </div>
    </div>
  </div>
</template>

<script>
const uEmojiParser = require('universal-emoji-parser')

export default {
  name: 'SummaryKDS',
  props: {
    orders: {
      type: Array,
      required: true
    },
    restaurant: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  computed: {
    aggregatedItems() {
      // Create a map to store items by display_string
      const itemsMap = new Map();
      
      // Process all orders
      this.orders.forEach(orderWrapper => {
        const order = orderWrapper;
        
        // Check if the order has hierarchical content
        if (order.orderContent && 
            order.orderContent.hierarchical && 
            order.orderContent.hierarchical.length > 0) {
          
          // Get filtered items based on device tag
          const filteredItems = (this.restaurant && this.restaurant.deviceTag)
            ? order.orderContent.hierarchical.filter(item => item.tag === this.restaurant.deviceTag)
            : order.orderContent.hierarchical;
          
          // Process each filtered hierarchical item
          filteredItems.forEach(item => {
            // Process parent item
            this.processItem(itemsMap, item);
            
            // Process child items if they exist
            if (item.items && item.items.length > 0) {
              item.items.forEach(subItem => {
                this.processItem(itemsMap, subItem);
              });
            }
          });
        }
      });
      
      // Convert map to array and sort by count (descending)
      return Array.from(itemsMap.values())
        .sort((a, b) => b.totalQuantity - a.totalQuantity);
    }
  },
  methods: {
    processItem(itemsMap, item) {
      // Skip items that are modifiers
      if (item.is_modifier === true) {
        return;
      }
      
      const key = item.display_string;
      const quantity = item.quantity || 1;
      
      if (itemsMap.has(key)) {
        // If item already exists, update the count
        const existingItem = itemsMap.get(key);
        existingItem.totalQuantity += quantity;
      } else {
        // Otherwise, add a new entry
        itemsMap.set(key, {
          display_string: key,
          icon: item.icon,
          totalQuantity: quantity
        });
      }
    },
    getEmoji(item) {
      if (item.icon) {
        return uEmojiParser.parse(item.icon);
      }
      return uEmojiParser.parse(':package:');
    }
  }
};
</script>

<style scoped>
.summary-container {
  background: var(--bz-p-0c1117);
  border-radius: 12px;
  padding: 10px 16px 8px 16px;
  color: var(--bz-p-white);
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  overflow: visible;
  margin-bottom: 0;
}

.summary-header {
  margin-bottom: 16px;
  text-align: center;
}

.summary-header h2 {
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: var(--bz-p-white);
}

.summary-content {
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-bottom: 4px;
  margin-bottom: 0;
  position: relative;
}

.summary-content::-webkit-scrollbar {
  display: none;
}

.items-list {
  display: flex;
  flex-direction: row;
  gap: 12px;
  padding-bottom: 12px;
  min-height: 40px;
}

.summary-item {
  display: flex;
  align-items: center;
  background: var(--bz-p-212b38);
  border-radius: 10px;
  padding: 8px 12px;
  transition: all 0.2s ease;
  flex: 0 0 auto;
  white-space: nowrap;
  box-shadow: 0px 2px 4px var(--bz-p-black-a10);
}

.summary-item:hover {
  background: var(--bz-p-2a3543);
}

.item-emoji {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 6px;
  font-size: 18px;
}

.item-count {
  background: var(--bz-p-blue-400);
  color: var(--bz-p-0c1117);
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 14px;
  font-weight: 800;
  margin-right: 8px;
  min-width: 28px;
  text-align: center;
}

.item-name {
  font-size: 15px;
  font-weight: 500;
}

.no-items {
  text-align: center;
  padding: 8px;
  font-style: italic;
  color: var(--bz-p-8899aa);
  font-size: 12px;
}

/* Mobile styles */
@media (max-width: 767px) and (orientation: portrait) {
  .summary-container {
    padding: 6px 10px;
    border-radius: 10px;
    margin-bottom: 5px;
  }
  
  .summary-header h2 {
    font-size: 16px;
  }
  
  .summary-item {
    padding: 5px 8px;
    border-radius: 8px;
  }
  
  .item-emoji {
    width: 18px;
    height: 18px;
    font-size: 14px;
    margin-right: 4px;
  }
  
  .item-count {
    font-size: 13px;
    min-width: 24px;
    padding: 1px 6px;
    margin-right: 6px;
  }
  
  .item-name {
    font-size: 13px;
  }
  
  .summary-content {
    padding-bottom: 6px;
  }
  
  .items-list {
    padding-bottom: 8px;
  }
}

/* Añadir indicadores de desplazamiento */
.summary-content::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 30px;
  background: linear-gradient(to right, transparent, var(--bz-p-0c1117));
  pointer-events: none;
  opacity: 0.7;
}
</style> 